import type { ConflictingEvent, ConflictsResponse } from "@/types/events.ts";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/http.ts";

export type ConflictStatus = "clear" | "conflict" | "loading";

export const useConflictDetection = ({
  startDateMs,
  endDateMs,
  isAllDay,
  externalEventId,
  userTimezone,
  enabled = true,
}: {
  startDateMs?: number;
  endDateMs?: number;
  isAllDay?: boolean;
  externalEventId?: string;
  userTimezone: string;
  enabled?: boolean;
}) => {
  const shouldFetch = enabled && !isAllDay && startDateMs != null && endDateMs != null;

  const { data, isLoading } = useQuery({
    queryKey: ["events", "conflicts", startDateMs, endDateMs, externalEventId],
    queryFn: async ({ signal }) => {
      const params: Record<string, string | number> = {
        startDateMs: startDateMs!,
        endDateMs: endDateMs!,
        userTimezone,
      };
      if (externalEventId) {
        params["excludeEventId"] = externalEventId;
      }
      const response = await api.get<ConflictsResponse>("/api/a/events/conflicts", {
        params,
        signal,
      });
      return response.data;
    },
    enabled: shouldFetch,
  });

  const conflicts: ConflictingEvent[] = data?.conflicts ?? [];

  let status: ConflictStatus;
  if (!shouldFetch) {
    status = "clear";
  } else if (isLoading) {
    status = "loading";
  } else if (conflicts.length > 0) {
    status = "conflict";
  } else {
    status = "clear";
  }

  return { status, conflicts };
};
